import {
  cashbookCustomNote,
  type CashbookSheetFooter,
  type CashbookSheetRow
} from '@finvox/shared/cashbook';
import { sheetToCsv } from '@finvox/shared/cashbook';
import { formatMoneyDisplay } from '@finvox/shared/money';
import autoTable from 'jspdf-autotable';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { buildExportHeaderHtml, type ExportHeaderOpts } from '@/lib/exportHeader';
import { ensureArabicPdfFont, hasArabicText } from '@/lib/pdfFonts';

export type CashbookExportInput = {
  orgName: string;
  header: ExportHeaderOpts;
  rows: CashbookSheetRow[];
  footer: CashbookSheetFooter | null;
  openingAmount?: string;
  openingDate?: string;
  filenameBase: string;
  includeBalanceColumn?: boolean;
  /** When true (Day view), include Notes column for custom memos. */
  includeNotesColumn?: boolean;
  labels: {
    colDate: string;
    colDescription: string;
    colNotes: string;
    colIn: string;
    colOut: string;
    colBalance: string;
    footerPeriodTotals: string;
    startingBalance: string;
    openingRow: string;
    payVoxNetSalesMemo: string;
    resolveEntryLabel: (label: string) => string;
  };
  isArabic?: boolean;
};

function activeExportRows(rows: CashbookSheetRow[]): CashbookSheetRow[] {
  return rows.filter((r) => !r.voided);
}

function rowDescription(row: CashbookSheetRow, openingRowLabel: string, input: CashbookExportInput): string {
  if (row.source === 'opening') return openingRowLabel;
  const key = row.categoryKey || row.label;
  return input.labels.resolveEntryLabel(key);
}

function rowNote(row: CashbookSheetRow, input: CashbookExportInput): string {
  const note = cashbookCustomNote(row.memo, row.categoryKey);
  return note ? input.labels.resolveEntryLabel(note) : '';
}

function includeBalance(input: CashbookExportInput): boolean {
  return input.includeBalanceColumn !== false;
}

function includeNotes(input: CashbookExportInput): boolean {
  return Boolean(input.includeNotesColumn);
}

function exportHeaders(input: CashbookExportInput): string[] {
  const headers = [input.labels.colDate, input.labels.colDescription];
  if (includeNotes(input)) headers.push(input.labels.colNotes);
  headers.push(input.labels.colIn, input.labels.colOut);
  if (includeBalance(input)) headers.push(input.labels.colBalance);
  return headers;
}

function exportMetaRows(input: CashbookExportInput): string[][] {
  const meta: string[][] = [[input.header.reportTitle], [input.header.periodLabel]];
  if (input.openingAmount && input.openingDate) {
    meta.push([
      `${input.labels.startingBalance}: ${formatMoneyDisplay(input.openingAmount)} (${input.openingDate})`
    ]);
  }
  return meta;
}

export function buildCashbookExportTableData(input: CashbookExportInput): string[][] {
  const fmt = (v: string) => formatMoneyDisplay(v);
  return activeExportRows(input.rows).map((r) => {
    const row = [r.date, rowDescription(r, input.labels.openingRow, input)];
    if (includeNotes(input)) {
      row.push(rowNote(r, input));
    }
    row.push(r.inAmount === '0.00' ? '' : fmt(r.inAmount), r.outAmount === '0.00' ? '' : fmt(r.outAmount));
    if (includeBalance(input)) row.push(fmt(r.balance));
    return row;
  });
}

function footerRowData(input: CashbookExportInput): string[] | undefined {
  if (!input.footer) return undefined;
  const row = ['', input.labels.footerPeriodTotals];
  if (includeNotes(input)) row.push('');
  row.push(formatMoneyDisplay(input.footer.totalIn), formatMoneyDisplay(input.footer.totalOut));
  if (includeBalance(input)) row.push(formatMoneyDisplay(input.footer.endBalance));
  return row;
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function buildCashbookCsvBlob(input: CashbookExportInput): Blob {
  const headers = exportHeaders(input);
  const data = buildCashbookExportTableData(input);
  const csv = sheetToCsv(headers, [...exportMetaRows(input), [], ...data], footerRowData(input));
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}

export function buildCashbookXlsxBlob(input: CashbookExportInput): Blob {
  const headers = exportHeaders(input);
  const sheetData: string[][] = [...exportMetaRows(input), []];
  sheetData.push(headers, ...buildCashbookExportTableData(input));
  const footer = footerRowData(input);
  if (footer) sheetData.push(footer);
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Cashbook');
  const bytes = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  return new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

function moneyColumnStyles(input: CashbookExportInput): Record<number, {halign: 'right'}> {
  let idx = 2 + (includeNotes(input) ? 1 : 0);
  const styles: Record<number, {halign: 'right'}> = {
    [idx]: {halign: 'right'},
    [idx + 1]: {halign: 'right'}
  };
  if (includeBalance(input)) styles[idx + 2] = {halign: 'right'};
  return styles;
}

async function buildCashbookPdfDoc(input: CashbookExportInput): Promise<jsPDF> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  const headerTexts = [
    input.header.reportTitle,
    input.header.titleAr,
    input.labels.startingBalance,
    input.labels.footerPeriodTotals,
    input.labels.colDate,
    input.labels.colDescription,
    input.labels.colNotes,
    input.labels.colIn,
    input.labels.colOut,
    input.labels.colBalance,
    input.labels.openingRow,
    ...buildCashbookExportTableData(input).flat()
  ];
  const needsArabic = headerTexts.some(hasArabicText);

  if (needsArabic) {
    await ensureArabicPdfFont(doc);
  }

  const latinFont = 'helvetica';
  const tableFont = needsArabic ? 'NotoSansArabic' : latinFont;

  doc.setFont(latinFont, 'normal');
  doc.setFontSize(14);
  doc.text(`FinVox — ${input.orgName}`, 40, 40);
  doc.setFontSize(10);
  doc.text(input.header.reportTitle, 40, 58);
  doc.text(input.header.periodLabel, 40, 72);

  if (needsArabic) {
    doc.setFont(tableFont, 'normal');
  }
  doc.text(input.header.titleAr, pageWidth - 40, 58, { align: 'right' });

  if (input.openingAmount && input.openingDate) {
    const openingLine = `${input.labels.startingBalance}: ${formatMoneyDisplay(input.openingAmount)} (${input.openingDate})`;
    doc.setFont(hasArabicText(openingLine) ? tableFont : latinFont, 'normal');
    doc.text(openingLine, 40, 88);
  }

  const tableBody = buildCashbookExportTableData(input);
  const footer = footerRowData(input);
  if (footer) tableBody.push(footer);

  autoTable(doc, {
    startY: input.openingAmount ? 100 : 84,
    head: [exportHeaders(input)],
    body: tableBody,
    styles: { font: tableFont, fontSize: 9, cellPadding: 4 },
    headStyles: { font: tableFont, fillColor: [245, 245, 245], textColor: [0, 0, 0] },
    columnStyles: moneyColumnStyles(input)
  });

  return doc;
}

export async function buildCashbookPdfBlob(input: CashbookExportInput): Promise<Blob> {
  const doc = await buildCashbookPdfDoc(input);
  return doc.output('blob');
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read export file'));
    reader.readAsDataURL(blob);
  });
}

export function downloadCashbookCsv(input: CashbookExportInput): void {
  downloadBlob(`${input.filenameBase}.csv`, buildCashbookCsvBlob(input));
}

export function downloadCashbookXlsx(input: CashbookExportInput): void {
  downloadBlob(`${input.filenameBase}.xlsx`, buildCashbookXlsxBlob(input));
}

export async function downloadCashbookPdf(input: CashbookExportInput): Promise<void> {
  const doc = await buildCashbookPdfDoc(input);
  doc.save(`${input.filenameBase}.pdf`);
}

/** @deprecated use buildExportHeaderHtml for HTML contexts */
export { buildExportHeaderHtml };
